import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { ChevronDown } from 'lucide-react'

import { useIsMobile } from '@/shared/hooks/useIsMobile'
import {
  ADDRESS_BIZ_TYPE_OPTIONS,
  BIZ_TYPE_TAB_LABELS,
  FLASH_PRODUCT_TABS,
  findFlashProductByBizType,
  isFlashBizType,
  isKnownAddressBizType,
} from '@/shared/lib/bizTypes'
import { getMethodLabel } from '@/shared/lib/format'

interface BizTypeFilterProps {
  value?: string
  onChange: (bizType: string | undefined) => void
}

const OPTIONS_TAB_LABEL = 'Options'

const BizTypeFilter: React.FC<BizTypeFilterProps> = ({ value, onChange }) => {
  const isMobile = useIsMobile()
  const [optionsOpen, setOptionsOpen] = useState(false)
  const optionsAnchorRef = useRef<HTMLDivElement>(null)
  const optionsButtonRef = useRef<HTMLButtonElement>(null)
  const optionsMenuRef = useRef<HTMLDivElement>(null)
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; minWidth: number } | null>(null)
  const activeValue = value?.trim().toUpperCase() || undefined
  const activeFlashProduct = activeValue ? findFlashProductByBizType(activeValue) : undefined
  const optionsActive = !!activeFlashProduct

  const updateDropdownRect = useCallback(() => {
    const button = optionsButtonRef.current
    if (!button) return
    const rect = button.getBoundingClientRect()
    setDropdownRect({
      top: rect.bottom + 4,
      left: rect.left,
      minWidth: Math.max(rect.width, isMobile ? 148 : 168),
    })
  }, [isMobile])

  useEffect(() => {
    if (!optionsOpen) {
      setDropdownRect(null)
      return
    }
    updateDropdownRect()
    const handleReposition = () => updateDropdownRect()
    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleReposition, true)
    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
    }
  }, [optionsOpen, updateDropdownRect])

  useEffect(() => {
    if (!optionsOpen) return
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (optionsAnchorRef.current?.contains(target)) return
      if (optionsMenuRef.current?.contains(target)) return
      setOptionsOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [optionsOpen])

  useEffect(() => {
    setOptionsOpen(false)
  }, [activeValue])

  const tabStyle = (active: boolean): React.CSSProperties => ({
    flexShrink: 0,
    padding: isMobile ? 'var(--space-md) var(--space-lg)' : 'var(--space-10) var(--space-xl)',
    borderRadius: '8px 8px 0 0',
    border: '1px solid var(--app-divider)',
    borderBottom: active ? '1px solid var(--app-card-bg)' : '1px solid var(--app-divider)',
    marginBottom: active ? '-1px' : 0,
    backgroundColor: active ? 'var(--brand-primary)' : 'var(--app-card-bg)',
    color: active ? 'var(--brand-on-primary)' : 'var(--app-text-primary)',
    fontSize: isMobile ? 'var(--font-size-sm)' : 'var(--font-size-md)',
    fontWeight: active ? 600 : 500,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    transition: 'color 0.15s, background-color 0.15s',
    position: 'relative',
    zIndex: active ? 2 : 0,
  })

  const phaseChipStyle = (active: boolean): React.CSSProperties => ({
    flexShrink: 0,
    padding: isMobile ? 'var(--space-sm) var(--space-lg)' : 'var(--space-sm) var(--space-14)',
    borderRadius: 'var(--radius-sm)',
    border: `1px solid ${active ? 'var(--brand-primary)' : 'var(--app-divider)'}`,
    backgroundColor: active ? 'var(--brand-primary)' : 'transparent',
    color: active ? 'var(--brand-on-primary)' : 'var(--app-text-secondary)',
    fontSize: isMobile ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
    fontWeight: active ? 600 : 500,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    transition: 'color 0.15s, background-color 0.15s, border-color 0.15s',
  })

  const dropdownItemStyle = (active: boolean): React.CSSProperties => ({
    display: 'block',
    width: '100%',
    padding: isMobile ? 'var(--space-10) var(--space-14)' : 'var(--space-10) var(--space-xl)',
    border: 'none',
    backgroundColor: active ? 'var(--brand-bg-subtle)' : 'transparent',
    color: active ? 'var(--brand-primary)' : 'var(--app-text-primary)',
    fontSize: isMobile ? 'var(--font-size-sm)' : 'var(--font-size-md)',
    fontWeight: active ? 600 : 500,
    textAlign: 'left',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background-color 0.15s, color 0.15s',
  })

  const coreTabs = ADDRESS_BIZ_TYPE_OPTIONS.map((option) => ({
    key: option,
    label: BIZ_TYPE_TAB_LABELS[option],
    value: option,
  }))

  const showUnknownTab = activeValue && !isKnownAddressBizType(activeValue) && !isFlashBizType(activeValue)

  const handleOptionsToggle = () => {
    setOptionsOpen((open) => !open)
  }

  const handleSelectFlashProduct = (productKey: (typeof FLASH_PRODUCT_TABS)[number]['key']) => {
    const product = FLASH_PRODUCT_TABS.find((item) => item.key === productKey)
    if (!product) return
    const current = product.phases.find((phase) => phase.bizType === activeValue)
    onChange(current?.bizType ?? product.phases[0]!.bizType)
    setOptionsOpen(false)
  }

  return (
    <div
      style={{
        borderBottom: '1px solid var(--app-divider)',
        backgroundColor: 'var(--app-bg)',
        position: 'relative',
        zIndex: 2,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 'var(--space-xs)',
          padding: isMobile ? 'var(--space-md) var(--space-md) 0' : 'var(--space-lg) var(--space-xl) 0',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
        }}
      >
        <button
          type="button"
          onClick={() => onChange(undefined)}
          style={tabStyle(!activeValue)}
          aria-pressed={!activeValue}
        >
          Transactions
        </button>
        {coreTabs.map((tab) => {
          const active = activeValue === tab.value
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => onChange(tab.value)}
              style={tabStyle(active)}
              aria-pressed={active}
            >
              {tab.label}
            </button>
          )
        })}
        <div ref={optionsAnchorRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button
            ref={optionsButtonRef}
            type="button"
            onClick={handleOptionsToggle}
            style={{
              ...tabStyle(optionsActive || optionsOpen),
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-sm)',
            }}
            aria-pressed={optionsActive}
            aria-expanded={optionsOpen}
            aria-haspopup="menu"
          >
            {OPTIONS_TAB_LABEL}
            {activeFlashProduct && <span style={{ opacity: 0.85, fontWeight: 500 }}>· {activeFlashProduct.label}</span>}
            <ChevronDown
              size={14}
              style={{
                opacity: 0.85,
                transform: optionsOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.15s',
              }}
            />
          </button>
        </div>
        {showUnknownTab && (
          <button type="button" onClick={() => onChange(activeValue)} style={tabStyle(true)} aria-pressed>
            {getMethodLabel(activeValue)}
          </button>
        )}
      </div>
      {optionsOpen &&
        dropdownRect &&
        createPortal(
          <div
            ref={optionsMenuRef}
            role="menu"
            style={{
              position: 'fixed',
              top: dropdownRect.top,
              left: dropdownRect.left,
              zIndex: 1000,
              minWidth: dropdownRect.minWidth,
              padding: 'var(--space-sm) 0',
              borderRadius: 'var(--radius-md)',
              border: '0.5px solid var(--app-card-border)',
              backgroundColor: 'var(--app-card-bg)',
              boxShadow: 'var(--shadow-dropdown)',
            }}
          >
            {FLASH_PRODUCT_TABS.map((product) => {
              const active = activeFlashProduct?.key === product.key
              return (
                <button
                  key={product.key}
                  type="button"
                  role="menuitem"
                  onClick={() => handleSelectFlashProduct(product.key)}
                  style={dropdownItemStyle(active)}
                  aria-pressed={active}
                >
                  {product.label}
                </button>
              )
            })}
          </div>,
          document.body,
        )}
      {activeFlashProduct && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-md)',
            padding: isMobile
              ? 'var(--space-md) var(--space-md) var(--space-10)'
              : 'var(--space-md) var(--space-xl) var(--space-lg)',
            borderTop: '1px solid var(--app-divider)',
            backgroundColor: 'var(--app-card-bg)',
          }}
        >
          <span
            style={{
              color: 'var(--app-text-secondary)',
              fontSize: isMobile ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
              fontWeight: 500,
              marginRight: 'var(--space-xs)',
            }}
          >
            {activeFlashProduct.label}
          </span>
          {activeFlashProduct.phases.map((phase) => {
            const active = activeValue === phase.bizType
            return (
              <button
                key={phase.key}
                type="button"
                onClick={() => onChange(phase.bizType)}
                style={phaseChipStyle(active)}
                aria-pressed={active}
              >
                {phase.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default BizTypeFilter
